import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from "prop-types";
import { onError, onSuccess, onWarning } from '../shared/messages';
import { GetReservationTotals, GetReservation, UpdateComments, UpdateReservationStatus } from '../../api/services/pms';
import Panel from '../shared/Panel';
import { useMessageResources, useTextResources } from '../../language';
import CurrencyDropDown from '../shared/CurrencyDropDown';
import { Form, Header, Segment, Icon, Button } from 'semantic-ui-react';
import moment from 'moment'
import './style.css'
import { ReservationStatusesEnums, ReservationStatusTypes } from '../shared/statustypes';
import _ from 'lodash'
import { Currencies } from '../shared/currencies';
import { ReactHTMLConverter } from 'react-html-converter/browser';
import HtmlEditorPopup from '../shared/HtmlEditor';
import { useLoader } from '../../hooks/useLoader';
import { Products } from '../../api/services/next';
import FolioPendingPopup from '../FolioPendingPopup'
import { dateTimeFormat } from '../shared/constants';
import { useConfirm } from '../../hooks/useConfirm';

const html = new ReactHTMLConverter()

const ReservationSummary = ({ ReservationID, HotelID, Currency, onReservation, reservation: initialReservation, readOnly, groupReservations }) => {
    const text = useTextResources()
    const loader = useLoader()
    const message = useMessageResources()
    const confirm  = useConfirm();
    const [totals, setTotals] = useState()
    const [reservation, setReservation] = useState(initialReservation)
    const [currency, setCurrency] = useState(Currency)
    const [showEditor, setShowEditor] = useState(false)
    const [errorState, setErrorState] = useState({
        showError: false
    })

    useEffect(() => {
        (async () => {
            if (initialReservation && !initialReservation.Product) {
                const products = await Products.GetProducts({ Id: initialReservation.ProductId })
                initialReservation.Product = products?.length && products[0]
            }
            setReservation(initialReservation)
        })()

    }, [initialReservation])

    const [showCurrencyDropDown, setShowCurrencyDropDown] = useState(true);

    useEffect(() => {
        if (currency) {
            setShowCurrencyDropDown(false)
        }
        if (ReservationID) {
            (async () => {
                try {
                    const reservation = await GetReservation({
                        IDs: [ReservationID],
                        HotelID,
                        IncludeReservationCompanyAgent: true,
                        IncludeAddons: true,
                        IncludeAddonsWithChain: true,
                        IncludeRoom: true,
                        ExcludeBlockReservation: false,
                        IncludeProduct: true,
                        IncludeRoomType: true,
                        IncludeReservationDailyAccommodations: true,
                        IncludeCanceled: true
                    })
                    setReservation(reservation)
                } catch (error) {
                    onError(error)
                }
            })()
        }
        window.addEventListener("message", function (event) {
            if (event.data && event.data.eventyType && event.data.eventyType === "CurrenyChanged") {
                setCurrency(event.data.newCurrency)
            }
        })
    }, [ReservationID])

    useEffect(() => {
        if (onReservation) {
            onReservation(reservation)
        }
    }, [reservation])

    useEffect(() => {
        if (ReservationID || initialReservation?.Id) {
            (async () => {
                try {
                    const totals = await GetReservationTotals({
                        ReservationIDs: [ReservationID || initialReservation.Id],
                        HotelId: HotelID || initialReservation.HotelId,
                        TargetCurrencyID: currency
                    })
                    setTotals(totals[0])
                } catch (error) {
                    onError(error)
                }
            })()
        }
    }, [ReservationID, initialReservation, currency])


    const format = useCallback(date => {
        return moment(date).format(dateTimeFormat)
    }, [])

    const status = useCallback(statusID => {
        return statusID ? text(ReservationStatusTypes[statusID]) : ""
    }, [])

    const price = useCallback(value => {
        const c = Currencies[currency]
        return `${value.toFixed(2)} ${c ? c.symbol : ''}`
    }, [currency])

    const addons = reservation ? _.uniq(_.map(reservation.ReservationDailyAddons, addon => {
        return addon.HotelAddon?.ChainAddon?.Name
    })) : []


    const Item = ({ title, subtitle, ...props }) => {
        return <div className="reservation-summary-column" {...props}>
            <Header size="tiny" {...props}>
                <Header.Subheader>{title}</Header.Subheader>
                <Header.Content>{subtitle}</Header.Content>
            </Header>
        </div>
    }

    Item.propTypes = {
        title: PropTypes.any,
        subtitle: PropTypes.any
    }

    const updateReservationStatus = async (reservation) => {
        loader(true)
        try {
            const updated = await UpdateReservationStatus({
                ...reservation,
                DayUseExecutePostBookingFlag: reservation.ForcedStatusUpdate && moment(reservation.CheckInDate).isSame(reservation.CheckOutDate, 'day')
            })
            setReservation(updated);
            onSuccess();
        } catch (err) {
            loader(false);
            if (err.status === 400) {
                if (err.error.IsRevocable) {
                    if (err.error.ErrorCode == 10167 || err.error.ErrorCode == 12354 || err.error.ErrorCode == 12367) {
                        setErrorState({
                            showError: true,
                            error: err.error
                        })
                    } else {
                        const errorMessageParsed = JSON.parse(err.error.ErrorMessage).message
                        const isRevocable = err.error.IsRevocable
                        await confirm({
                            header: text('StatusCouldNotBeChanged'),
                            content: errorMessageParsed,
                            confirmButton: {
                                content: isRevocable ? 'Yes' : 'Close',
                                positive: true,
                            },
                            cancelButton: {
                                content: isRevocable ? 'No' : null,
                                negative: true,
                            },
                            onConfirm: () =>
                                isRevocable ? updateReservationStatus({...reservation, ForcedStatusUpdate: true}) : void 0,
                        });
                    }
                } else {
                    onWarning(err);
                }
            } else {
                onError(err);
            }
            return null;
        } finally {
            loader(false)
        }
    }

    const saveComments = async value => {
        try {
            await UpdateComments({
                Id: reservation.Id,
                HotelId: reservation.HotelId,
                Comments: value
            })
            setReservation({
                ...reservation,
                Comments: value
            })
            setShowEditor(false)
            onSuccess("CommentsUpdated")
        } catch (error) {
            onError(error)
        }
    }

    const getReservationLink = () => {
        const isSelfie = window.location.search.includes("IsSelfie=1")
        let url = ""
        if (isSelfie) {
            url = `${window.optima?.$optimaUrls?.Selfie || ""}/Reception#/orderdetails/${reservation.Id}`
        } else {
            url = `${window.optima?.$optimaUrls?.Shell || ""}PMS/Reservations/Index/${reservation.Id}?hotelid=${reservation.HotelId}`
        }
        const title = groupReservations ? `${text("MainReservation")}: ${reservation.Number}` : `${text("ReservationNumber")}: ${reservation.Number}`
        return <a href={url}>{title}</a>
    }

    const DoReservationCheckout = async () => {
        const isEarlyCheckout = moment(window.user?.CurrentHotel?.HotelParameter?.CurrentBusinessDate).startOf("day").isBefore(moment(reservation.CheckOutDate).startOf("day"))
        if (reservation.StatusId !== ReservationStatusesEnums.CheckedIn) {
            onWarning(message("EarlyCheckoutIsNotPossible"));
            return;
        }
        else if (
            isEarlyCheckout && !(await confirm({
                header: text("EarlyCheckOut"),
                content: `${text("WouldYouLikeTo")} ${text("PerformAnEarlyCheckOutToReservation")} ${text("Number")} ${reservation.Number}?`,
            }))
        )
            return;
        updateReservationStatus({
            ...reservation,
            StatusId: ReservationStatusesEnums.CheckedOut
        })
    }

    return <div className="ReservationSummary" >
        <Panel
            header={text("ReservationSummary")}
            subheader={reservation ? getReservationLink() : `text("ReservationNumber")}:`}
            attached icon="clipboard outline"
            segments={[
                <Segment attached secondary style={{ borderTop: "none" }} key="totals">
                    {totals && <div className="totals">
                        <div className="reservation-summary-row">
                            <Item title={text("TotalPrice")} subtitle={price(totals.TotalAmountDebitAfterTax)}></Item>
                        </div>
                        <div className="reservation-summary-row">
                            <Item title={text("Tax")} subtitle={price(totals.TotalAmountDebitAfterTax - totals.TotalAmountDebitBeforeTax)}></Item>
                        </div>
                        <div className="reservation-summary-row">
                            <Item title={text("Payment")} subtitle={price(totals.TotalAmountCreditAfterTax)}></Item>
                        </div>
                        <div className="reservation-summary-row">
                            <Item color="red" title={text("Balance")} subtitle={price(totals.TotalAmountDebitAfterTax + totals.TotalAmountCreditAfterTax)}></Item>
                        </div>
                    </div>}
                </Segment>,
                <Segment attached="bottom" style={{ borderTop: "none" }} key="comments">
                    <Header size="tiny">
                        <Header.Subheader>
                            {text("Comments")}
                            <Icon name='pencil' style={{ float: "right" }} link onClick={() => setShowEditor(true)} />
                        </Header.Subheader>
                        <div style={{ whiteSpace: "initial", maxHeight: "6em", overflowY: "auto", overflowX: "hidden" }}>{reservation ? html.convert(reservation.Comments) : ""}</div>
                    </Header>
                </Segment>
            ]}
        >
            {
                showCurrencyDropDown &&
                <Form>
                    <Form.Field control={CurrencyDropDown} label={text("SelectCurrency")} textField="Name" descriptionField="Symbol" onChange={setCurrency} value={currency || ''} />
                </Form>
            }
            {reservation && <div className="details">
                <div className="reservation-summary-row">
                    <Item title={text("ReservationStatus")} subtitle={status(reservation.StatusId)}></Item>
                    {(reservation.StatusId === ReservationStatusesEnums.Reserved || reservation.StatusId === ReservationStatusesEnums.PreCheckin) && !readOnly && <Item subtitle={<Button onClick={() => updateReservationStatus({
                        ...reservation,
                        StatusId: ReservationStatusesEnums.CheckedIn
                    })}>{text("CheckIn")}</Button>}></Item>}
                    {reservation.StatusId === ReservationStatusesEnums.CheckedIn && !readOnly && <Item subtitle={<Button onClick={DoReservationCheckout}>{text("CheckOut")}</Button>}></Item>}
                </div>
                <div className="reservation-summary-row">
                    <Item title={text("CheckIn")} subtitle={format(reservation.CheckInDate)}></Item>
                    <Item title={text("CheckOut")} subtitle={format(reservation.CheckOutDate)}></Item>
                </div>
                <div className="reservation-summary-row">
                    <Item title={text("Nights")} subtitle={reservation.Nights}></Item>
                    <Item title={text("Guests")} subtitle={`${reservation.Adults} / ${reservation.Children} / ${reservation.Infants}`}></Item>
                </div>
                <div className="reservation-summary-row">
                    <Item title={text("Company")} subtitle={reservation.Company ? reservation.Company.Name : "-"}></Item>
                    <Item title={text("Agent")} subtitle={reservation.Agent ? reservation.Agent.Name : "-"}></Item>
                </div>
                <div className="reservation-summary-row">
                    <Item title={text("Product")} colSpan={1} subtitle={reservation.Product ? reservation.Product.Name : "-"}></Item>
                    <Item title={text("Room")} subtitle={reservation.Room ? reservation.Room.Name : "-"}></Item>
                </div>
                <div className="reservation-summary-row">
                    <Item colSpan={2} title={text("Addons")} subtitle={addons.length ? _.join(addons, ", ") : "-"}></Item>
                </div>
            </div>}
        </Panel>

        <HtmlEditorPopup title={text("ReservationComments")} value={reservation ? reservation.Comments : ''}
            startOpen={showEditor} onCancel={() => setShowEditor(false)} onSave={saveComments} />

        <FolioPendingPopup
            error={errorState.error}
            onCancel={() => setErrorState({ showError: false })}
            onConfirm={() => {
                setErrorState({ showError: false })
                updateReservationStatus({ ...reservation, ForcedStatusUpdate: true, StatusId: ReservationStatusesEnums.CheckedOut })
            }} />
    </div>
}

ReservationSummary.propTypes = {
    ReservationID: PropTypes.number,
    FolioId: PropTypes.number,
    HotelID: PropTypes.number,
    Currency: PropTypes.number,
    onReservation: PropTypes.func,
    reservation: PropTypes.object,
    readOnly: PropTypes.bool,
    groupReservations: PropTypes.array
}
export default ReservationSummary